import React from 'react';
import { KiteIcon } from '@kite/react-kite';
import classNames from 'classnames';
import { networkClassNameMap } from '../../utils/networkClassNameMap';
import { networkLogoClassNameMap } from '../../utils/networkLogoClassNameMap';
import { useVisitId } from '../../hooks/useVisitId';
import useContextToken from '../../hooks/useContextToken';

import './NetworkBanner.scss';

import { INetwork } from '../../utils/models';

export interface INetworkBannerProps {
  network: INetwork;
  tmsId: string;
  maxLogoUrl?: string;
  badges: {
    id: string;
    url: string;
    live_tv_app: {
      width: string;
      height: string;
      image: {
        url: string;
      };
    };
  }[];
}

const NetworkBanner = ({
  network: { displayName, networkImgUrl, description },
  tmsId,
  badges,
  maxLogoUrl,
}: INetworkBannerProps) => {
  const visitId = useVisitId();
  const { contextToken } = useContextToken();
  const networkClassName = networkClassNameMap[displayName] || '';
  const networkLogoClassName = networkLogoClassNameMap[displayName] || '';
  const bannerLogoUrl =
    displayName === 'Max'
      ? maxLogoUrl
      : `${networkImgUrl}&sourceType=colorHybrid`;

  return (
    <div className="network-banner__wrapper">
      <div
        className={classNames({
          'network-banner': true,
          'network-banner__mobile--no-live-tv': !tmsId,
          [networkLogoClassName]:
            displayName in networkLogoClassNameMap ? true : false,
        })}
      >
        <img
          className={classNames({
            'network-banner__image': true,
            'network-banner__image--max': displayName === 'Max',
          })}
          src={bannerLogoUrl}
          alt={`${displayName} logo`}
          width="250"
          height="250"
        />
        <p
          className={classNames({
            'network-banner__description': true,
            'network-banner__description--hidden': tmsId,
            'network-banner__description--show-mobile': !tmsId,
            [networkClassName]:
              displayName in networkClassNameMap ? true : false,
          })}
        >
          {description}
        </p>
        <div
          className={classNames({
            'network-banner__live-tv-card': true,
            'network-banner__live-tv-card--hidden': !tmsId,
            'network-banner__live-tv-card--display-none': !tmsId,
          })}
        >
          <KiteIcon
            id="network-banner-tv-icon"
            className="network-banner__live-tv-card--tv-icon"
            ariaLabel="Live TV icon"
            fill="fff"
            icon="ki-live-tv"
            size="5rem"
          />
          <a
            className="network-banner__live-tv-card--link"
            href={`https://watch.spectrum.net/livetv?tmsid=${tmsId}&visitId=${visitId}${contextToken}`}
            target="_blank"
            rel="noreferrer"
          >
            Watch Live TV{' '}
            <KiteIcon
              id="network-banner-link-icon"
              icon="ki-link"
              inline={true}
            />
          </a>
        </div>
      </div>
      {badges?.length > 0 && (
        <ul className="network-banner__live-tv-apps">
          {badges.map((badge) => {
            const { url: appUrl, live_tv_app } = badge;
            const { url: imageUrl } = live_tv_app.image[0];
            const { width, height } = badge.live_tv_app;
            return (
              <li key={badge.id}>
                <a href={appUrl} target="_blank" rel="noreferrer">
                  <img
                    className="network-banner__live-tv-apps--logo"
                    src={imageUrl}
                    alt="App logo"
                    width={width}
                    height={height}
                  />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NetworkBanner;
